// How a lab result's interpretation is shown (docs/plans/phase1-lab.md §5.6; DESIGN.md §7:
// "Abnormal lab values: bold, plus H/L/HH/LL marker in its own column. Critical: danger
// text and marker").
//
// The interpretation itself is computed by the server from the clinical lead's ranges or
// answer options; this module only names it. The marker is never the only signal: every
// marker has a word for screen readers and for the printed key.

export const LAB_INTERPRETATIONS = [
  'normal',
  'low',
  'high',
  'critical_low',
  'critical_high',
  /** A coded answer the clinical lead marked abnormal. */
  'abnormal',
  /** A coded answer the clinical lead marked critical. */
  'critical',
  /** No range is configured for this analyte, age and sex. */
  'no_range',
  /** Text results, "not done", and censored values a range can't judge. */
  'not_applicable',
] as const;
export type LabInterpretation = (typeof LAB_INTERPRETATIONS)[number];

export type LabFlagMarker = '' | 'L' | 'H' | 'LL' | 'HH' | 'A' | 'AA';

interface FlagPresentation {
  readonly marker: LabFlagMarker;
  /** The words beside or instead of the marker. Empty when nothing is flagged. */
  readonly word: string;
  /** Bold on screen and on paper. */
  readonly emphasised: boolean;
  /** Shown in `danger`: the only use of that colour in the lab screens. */
  readonly critical: boolean;
}

const PRESENTATION: Record<LabInterpretation, FlagPresentation> = {
  normal: { marker: '', word: '', emphasised: false, critical: false },
  low: { marker: 'L', word: 'Low', emphasised: true, critical: false },
  high: { marker: 'H', word: 'High', emphasised: true, critical: false },
  critical_low: { marker: 'LL', word: 'Critical low', emphasised: true, critical: true },
  critical_high: { marker: 'HH', word: 'Critical high', emphasised: true, critical: true },
  abnormal: { marker: 'A', word: 'Abnormal', emphasised: true, critical: false },
  critical: { marker: 'AA', word: 'Critical', emphasised: true, critical: true },
  no_range: { marker: '', word: 'No range set', emphasised: false, critical: false },
  not_applicable: { marker: '', word: '', emphasised: false, critical: false },
};

export function labFlag(interpretation: LabInterpretation): FlagPresentation {
  return PRESENTATION[interpretation];
}

export function isCriticalLabInterpretation(interpretation: LabInterpretation): boolean {
  return PRESENTATION[interpretation].critical;
}

/** Outside the reference range or an abnormal answer, critical included. */
export function isAbnormalLabInterpretation(interpretation: LabInterpretation): boolean {
  return PRESENTATION[interpretation].emphasised;
}
